<template>
  <Layout>
    <PageHeader :title="'Shop'" :items="breadcrumbs" />
    <div class="member-shop">
      <!-- Loading state: skeleton cards -->
      <b-row v-if="memberOrderingLoading">
        <b-col
          v-for="n in 6"
          :key="'skeleton-' + n"
          cols="12"
          md="6"
          lg="4"
          class="mb-3"
        >
          <div class="skeleton-card"></div>
        </b-col>
      </b-row>

      <!-- Products grid -->
      <b-row v-else-if="memberProducts.length > 0">
        <b-col
          v-for="product in memberProducts"
          :key="product.id"
          cols="12"
          md="6"
          lg="4"
          class="mb-3"
        >
          <member-product-card
            :product="product"
            :cart-quantity="getCartQuantity(product.id)"
            @add-to-cart="handleAddToCart"
            @update-cart-quantity="handleUpdateQuantity"
            @view-details="onViewDetails"
          />
        </b-col>
      </b-row>

      <!-- Empty state -->
      <div
        v-else
        class="text-center py-5"
      >
        <i class="bx bx-store" style="font-size: 48px; color: var(--text-muted)"></i>
        <p class="text-muted mt-3">No products available at the moment.</p>
      </div>
    </div>

    <cart-bottom-nav
      active-tab="shop"
      :cart-count="cartItemCount"
      @navigate="handleNavigate"
    />

    <!-- Product Detail Modal -->
    <product-detail-modal
      :product="selectedProduct"
      action-label="Add to Cart"
      @add-product="onAddFromModal"
      @closed="selectedProduct = null"
    />
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import MemberProductCard from "@/components/member/memberProductCard";
import CartBottomNav from "@/components/member/cartBottomNav";
import ProductDetailModal from "@/components/shared/productDetailModal";
import {
  memberOrderingComputed,
  memberOrderingMethods,
} from "@/state/helpers";

export default {
  page: {
    title: "Shop",
  },
  components: { Layout, PageHeader, MemberProductCard, CartBottomNav, ProductDetailModal },
  data() {
    return {
      selectedProduct: null,
    };
  },
  computed: {
    ...memberOrderingComputed,
    breadcrumbs() {
      return [{ text: "Shop", active: true }];
    },
  },
  methods: {
    ...memberOrderingMethods,
    getCartQuantity(productId) {
      const item = this.cartItems.find((i) => i.product.id === productId);
      return item ? item.quantity : 0;
    },
    handleAddToCart({ productId, quantity }) {
      const product = this.memberProducts.find((p) => p.id === productId);
      if (product) {
        this.addToCart({ product, quantity });
      }
    },
    handleUpdateQuantity({ productId, newQty }) {
      if (newQty < 1) {
        this.removeFromCart(productId);
      } else {
        this.updateCartQuantity({ productId, quantity: newQty });
      }
    },
    onViewDetails(product) {
      this.selectedProduct = product;
      this.$nextTick(() => {
        this.$bvModal.show('productDetailModal');
      });
    },
    onAddFromModal(product) {
      this.handleAddToCart({ productId: product.id, quantity: 1 });
      this.$bvModal.hide('productDetailModal');
    },
    handleNavigate(tab) {
      const routes = {
        shop: "member-shop",
        cart: "member-cart",
        orders: "member-orders",
        profile: "member-profile",
      };
      if (routes[tab] && this.$route.name !== routes[tab]) {
        this.$router.push({ name: routes[tab] });
      }
    },
  },
  created() {
    this.fetchProducts();
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/surfaces";

.member-shop {
  padding-bottom: 64px;
  animation: member-fade-in 150ms ease;
}

@keyframes member-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.skeleton-card {
  height: 280px;
  background: $surface-elevated;
  border-radius: 4px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
</style>
